import { Component, OnInit, ViewChild } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/finally';
import * as $ from 'jquery';

import { AuthService } from '../../services/auth.service';
import { CommonService } from '../../services/common.service';
import { CompanyService } from '../../services/company.service';
import { ToolService } from '../../services/tool.service';

import { ICompany } from '../../models/ICompany.model';
import { ICompanyContact } from '../../models/ICompanyContact.model';
import { IEmail } from '../../models/IEmail.model';
import { IPhone } from '../../models/IPhone.model';
import { IPhoneType } from '../../models/IPhoneType.model';
import { IScheduleGroup } from '../../models/IScheduleGroup.model';
import { SaveAccountPage } from '../../models/SaveAccountPage.model';
import { SaveCompanyContact } from '../../models/SaveCompanyContact.model';
import { User, UserType } from '../../models/User.model';
import { ImpersonationData } from '../../models/ImpersonationData';

@Component({
    selector: 'app-account',
    templateUrl: './account.component.html',
    styleUrls: ['./account.component.less']
})
export class AccountComponent implements OnInit {
    private user: LoginInfo;
    private companies: ICompany[];
    private originalData: IAccountPageData;
    private companiesMap: object;
    private origCompaniesMap: object;

    impersonating: ImpersonationData = null;
    changingPassword = false;
    passwordConfirm = '';
    passwordMatched = true;
    usernameValid = false;
    $alertUpdated: any = null;
    phoneTypes: IPhoneType[];
    loggingOut = false;
    userType = UserType;

    @ViewChild('accountForm') form: any;

    constructor(
        private _auth: AuthService,
        private _common: CommonService,
        private _company: CompanyService,
        private _tools: ToolService
    ) { }

    private checkDefaultCompany(): void {
        if (!this.companies.length) return;

        let hasDefault = false;

        this.companies.forEach(company => {
            if (company.IsDefault) {
                hasDefault = true;
                return;
            }
        });

        if (!hasDefault) this.companies[0].IsDefault = true;
    }

    private createArrayMap(companies: ICompany[]): object {
        if (!(companies || [] as ICompany[]).length) return {};

        return companies.reduce((agg, curr, i) => {
            agg[curr.CompanyContactId] = i;
            return agg;
        }, {});
    }

    private initialize(): void {
        this.user = this._auth.getUser() as LoginInfo;
        this.impersonating = this._auth.getImpersonationData();
        let companiesRequest;
        if (this.impersonating) {
            const contactId = this._auth.getImpersonationData().ImpersonatedId;
            companiesRequest = this._company.recruiterGetCompanies(contactId);
        } else {
            companiesRequest = this._company
                .getCompanies();
        }

        companiesRequest
            .subscribe((companies: ICompany[]) => {
                this.companies = companies;
                this.originalData = {
                    user: this._tools.clone(this.user),
                    companies: this._tools.clone(this.companies)
                };
                this.companiesMap = this.createArrayMap(this.companies);
                this.origCompaniesMap = this.createArrayMap(this.originalData.companies);
                this.checkDefaultCompany();

                if (this.companies.length === 1) {
                    this.toggleExpandCompany(this.companies[0]);
                }
            });

        this._common.getPhoneTypes()
            .subscribe(result => {
                this.phoneTypes = result;
            });

        sessionStorage.setItem('loggingOut', 'false');
    }

    private setCompanyContact(companyContactId: number, contact: ICompanyContact): void {
        const currentCompany = this.companies[this.companiesMap[companyContactId]];
        if (currentCompany) currentCompany.Contact = contact;

        const days = ['M', 'T', 'W', 'Th', 'F', 'S', 'Su'];

        currentCompany.Contact.GroupedSchedules.map(s => {
            const selected = s.ScheduleDay.split(',')
            const ids = s.CompanyContactScheduleId.split(',')

            s.DaySelected = {}
            s.HasId = {}

            days.map(d => {
                s.DaySelected[d] = selected.includes(d)
                s.HasId[d] = s.DaySelected[d] ? parseInt(ids[selected.indexOf(d)], 10) : null;
            });

            s.AllDay = !s.ScheduleTimeFrom && !s.ScheduleTimeTo
        });

        const origCompany = this.originalData.companies[this.origCompaniesMap[companyContactId]];
        if (origCompany) origCompany.Contact = this._tools.clone(contact);

        if (!currentCompany.Contact.Emails.length) {
            this.addEmail(currentCompany);
        }

        if (!currentCompany.Contact.Phones.length) {
            this.addPhone(currentCompany);
        }
    }

    private toggleExpandCompany(company: ICompany) {
        company.Expanded = !company.Expanded;

        if (company.Expanded && !company.Contact) {
            let contactRequest;
            if (this._auth.getImpersonationData()) {
                contactRequest = this._company
                    .recruiterGetContact(company.CompanyContactId);
            } else {
                contactRequest = this._company.getContact(company.CompanyContactId);
            }

            contactRequest.subscribe(contact => this.setCompanyContact(company.CompanyContactId, contact));
        }
    }

    private getDifferentPhones(diff: SaveCompanyContact, initial: IPhone[], current: IPhone[]): void {
        const idField = 'CompanyContactPhoneId';
        const initialDict = this._tools.toDictionary(idField, initial);
        const currentDict = this._tools.toDictionary(idField, current);

        const allIds = Array.from(new Set(Object.keys(initialDict).concat(Object.keys(currentDict)).map(s => parseInt(s, 10))));

        for (let i = 0; i < allIds.length; i++) {
            const id = allIds[i];
            if (currentDict[id]) {
                if (initialDict[id].Number !== currentDict[id].Number
                    || initialDict[id].Extension !== currentDict[id].Extension
                    || initialDict[id].PhoneTypeId !== currentDict[id].PhoneTypeId) {
                    diff.UpdatedPhones.push(currentDict[id]);
                }
            } else diff.DeletedPhones.push(id);
        }

        for (let i = 0; i < current.length; i++) {
            if (!current[i][idField]) {
                diff.UpdatedPhones.push(current[i]);
            }
        }
    }

    private getDifferentEmails(diff: SaveCompanyContact, initial: IEmail[], current: IEmail[]): void {
        const idField = 'CompanyContactEmailId';
        const initialDict = this._tools.toDictionary(idField, initial);
        const currentDict = this._tools.toDictionary(idField, current);

        const allIds = Array.from(new Set(Object.keys(initialDict).concat(Object.keys(currentDict)).map(s => parseInt(s, 10))));

        for (let i = 0; i < allIds.length; i++) {
            if (currentDict[allIds[i]]) {
                if (initialDict[allIds[i]].Email !== currentDict[allIds[i]].Email) {
                    diff.UpdatedEmails.push(currentDict[allIds[i]]);
                }
            } else diff.DeletedEmails.push(allIds[i]);
        }

        for (let i = 0; i < current.length; i++) {
            if (!current[i][idField]) {
                diff.UpdatedEmails.push(current[i]);
            }
        }
    }

    private getUpdatedSchedules(diff: SaveCompanyContact, current: IScheduleGroup[]): void {
        const days = ['M', 'T', 'W', 'Th', 'F', 'S', 'Su'];

        current.forEach(function (s) {
            if (s.isDeleted) {
                days.forEach(function (d) {
                    if (s.HasId[d])
                        diff.DeletedSchedules.push(s.HasId[d]);
                });
            } else {
                days.forEach(function (d) {
                    if (s.HasId[d] && !s.DaySelected[d]) {
                        diff.DeletedSchedules.push(s.HasId[d]);
                    } else if (s.DaySelected[d]) {
                        const schedule = {
                            ScheduleDay: d,
                            CompanyContactScheduleId: s.HasId[d],
                            ScheduleTimeFromDate: null,
                            ScheduleTimeToDate: null,
                            ScheduleTimeFrom: s.ScheduleTimeFrom,
                            ScheduleTimeTo: s.ScheduleTimeTo,
                            AllDay: s.AllDay
                        }
                        diff.UpdatedSchedules.push(schedule);
                    }
                })
            }
        })
    }

    private getDifferences(initial: ICompany, current: ICompany): SaveCompanyContact | null {
        if (initial.CompanyContactId !== current.CompanyContactId) throw Error('trying to compare different companies');

        const diff = new SaveCompanyContact();

        diff.CompanyContactId = initial.CompanyContactId;

        if (initial.IsDefault !== current.IsDefault && current.IsDefault) diff.IsDefault = true;

        if (!initial.Contact || !current.Contact) {
            if (diff.IsDefault) return diff;
            else return null;
        }

        if (initial.Contact.FirstName !== current.Contact.FirstName
            || initial.Contact.LastName !== current.Contact.LastName
            || initial.Contact.NickName !== current.Contact.NickName
            || initial.Contact.Title !== current.Contact.Title
            || initial.Contact.TimeZoneId !== current.Contact.TimeZoneId) {
            diff.FirstName = current.Contact.FirstName;
            diff.LastName = current.Contact.LastName;
            diff.NickName = current.Contact.NickName;
            diff.TimeZoneId = current.Contact.TimeZoneId;
            diff.Title = current.Contact.Title;
        }

        diff.UpdatedPhones = [];
        diff.DeletedPhones = [];
        diff.UpdatedEmails = [];
        diff.DeletedEmails = [];
        diff.UpdatedSchedules = [];
        diff.DeletedSchedules = [];

        this.getDifferentPhones(diff, initial.Contact.Phones, current.Contact.Phones);
        this.getDifferentEmails(diff, initial.Contact.Emails, current.Contact.Emails);
        this.getUpdatedSchedules(diff, current.Contact.GroupedSchedules);

        if (!diff.UpdatedPhones.length) delete diff.UpdatedPhones;
        if (!diff.DeletedPhones.length) delete diff.DeletedPhones;
        if (!diff.UpdatedEmails.length) delete diff.UpdatedEmails;
        if (!diff.DeletedEmails.length) delete diff.DeletedEmails;
        if (!diff.UpdatedSchedules.length) delete diff.UpdatedSchedules;
        if (!diff.DeletedSchedules.length) delete diff.DeletedSchedules;

        return diff;
    }

    private getChangedInfo(): SaveAccountPage | null {
        const dataToSave = new SaveAccountPage();

        dataToSave.Contacts = [];

        if (this.user.Username && this.user.Password
            && (this.user.Username !== this.originalData.user.Username || this.user.Password !== this.originalData.user.Password)) {
            dataToSave.UserName = this.user.Username;
            dataToSave.Password = this.user.Password;
        }

        const initialCompanies = this._tools.toDictionary('CompanyContactId', this.originalData.companies);
        const currentCompanies = this._tools.toDictionary('CompanyContactId', this.companies);

        const companiesIds = Object.keys(initialCompanies);

        for (let i = 0; i < companiesIds.length; i++) {
            const diff = this.getDifferences(initialCompanies[companiesIds[i]], currentCompanies[companiesIds[i]]);
            if (diff) dataToSave.Contacts.push(diff);
        }

        return dataToSave;
    }

    contactChanged(contact: ICompanyContact): void {
        this.form.form.markAsDirty();
    }

    cancel(): void {
        this.form.form.markAsPristine();
        this.changingPassword = false;
        this.passwordMatched = true;
        this.initialize();
    }

    save(): void {
        if (this.form.invalid) {
            return;
        }

        const changedInfo = this.getChangedInfo();

        if (!changedInfo) return;

        const calls = [];

        if (changedInfo.UserName && changedInfo.Password) {
            calls.push(this._auth.updateLoginInfo(changedInfo.UserName, changedInfo.Password));
        }

        if ((changedInfo.Contacts || []).length) calls.push(this._company.saveContacts(changedInfo.Contacts));

        let changedDefault = false;
        for (let i = 0; i < changedInfo.Contacts.length; i++) {
            if (changedInfo.Contacts[i].IsDefault) {
                changedDefault = true;
                break;
            }
        }

        if (!calls.length) return;


        Observable
            .forkJoin(calls)
            .finally(() => {
                this.form.form.markAsPristine();
                this.changingPassword = false;

                if (changedDefault && this._auth.getUser().Type === UserType.Client) {
                    this._company.updateRecruiterInfo();
                }

                this.showSaveMessage();
            })
            .subscribe(([notused, contacts]) => {
                const contactsArr = contacts as ICompanyContact[];
                if (!(contactsArr || [] as ICompanyContact[]).length) return;

                for (let i = 0; i < contactsArr.length; i++) {
                    this.setCompanyContact(contactsArr[i].CompanyContactId, contactsArr[i]);
                }
            });
    }

    showSaveMessage(): void {
        this.$alertUpdated.addClass('show');
    }

    checkUsername(username: string): void {
        this._auth
            .checkUsername(username)
            .subscribe(result => {
                if (result) {
                    this.form.form.controls.username.setErrors({ 'duplicate': true });
                    this.usernameValid = false;
                } else {
                    if (this.form.form.controls.username.errors) {
                        this.form.form.controls.username.errors.duplicate = false;
                    }
                    this.usernameValid = true;
                }
            });
    }

    checkPassword(): void {
        this.passwordMatched = this.user.Password === this.passwordConfirm;
    }

    togglePassword(): void {
        const input = document.getElementById('password');
        const input2 = document.getElementById('password2');

        if (input.getAttribute('type') === 'password') {
            input.setAttribute('type', 'text');
            input2.setAttribute('type', 'text');
        } else {
            input.setAttribute('type', 'password');
            input2.setAttribute('type', 'password');
        }
    }

    addPhone(company: ICompany): void {
        company.Contact.Phones.push({
            CompanyContactPhoneId: null,
            Number: null,
            Extension: null,
            PhoneTypeId: 1
        });
    }

    deletePhone(contact: ICompanyContact, phone: IPhone): void {
        for (let i = 0; i < contact.Phones.length; i++) {
            if (phone === contact.Phones[i]) {
                contact.Phones.splice(i, 1);
                break;
            }
        }

        if (this.form.pristine) this.contactChanged(contact);
    }

    addEmail(company: ICompany): void {
        company.Contact.Emails.push({
            CompanyContactEmailId: null,
            Email: null
        });
    }

    deleteEmail(contact: ICompanyContact, email: IEmail): void {
        for (let i = 0; i < contact.Emails.length; i++) {
            if (email === contact.Emails[i]) {
                contact.Emails.splice(i, 1);
                break;
            }
        }

        if (this.form.pristine) this.contactChanged(contact);
    }

    trimPhone(tel: string): string {
        return tel.replace(/\D+/g, '');
    }

    toggleDefaultCompany(companyContactId: number) {
        this.companies.forEach(function (company) {
            if (company.CompanyContactId !== companyContactId) {
                company.IsDefault = false;
            }
        });
    }

    ngOnInit() {
        this.initialize();

        this.$alertUpdated = $('#alert-updated');
        this.$alertUpdated.on('closes.bs.alert', function () {
            this.$alertUpdated.hide();
            return false;
        });
    }

    canDeactivate() {
        if (sessionStorage.getItem('loggingOut') === 'true' || this.form.pristine) {
            return true;
        } else {
            return confirm('You have unsaved information. Discard changes and leave this page?');
        }
    }
}

class LoginInfo extends User {
    Password: string | undefined;
}

interface IAccountPageData {
    user: LoginInfo;
    companies: ICompany[];
}



// WEBPACK FOOTER //
// ./src/app/components/account/account.component.ts